import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

import {
  flyoutMargin,
  hoverBridgeBox,
  hoverBridgeHorizontal,
  placeSubmenuAboveFooter,
  SUBMENU_EDGE_GAP,
  SUBMENU_VISUAL_GAP,
} from "./submenuPlacement";
import { getVisibleBottomBarRect } from "../../layout/visibleBottomBar";

export const ACTIONS_MENU_LAYER = "data-actions-menu-layer";

export function isInsideActionsMenu(target: EventTarget | null, menuRoot: HTMLElement | null) {
  if (!(target instanceof Element)) return false;
  if (menuRoot?.contains(target)) return true;
  return target.closest(`[${ACTIONS_MENU_LAYER}]`) != null;
}

function useAboveFooterPlacement(anchor: HTMLDivElement, panelRef: RefObject<HTMLDivElement | null>) {
  const [placement, setPlacement] = useState({
    panelLeft: 0,
    panelTop: 0,
    maxHeight: undefined as number | undefined,
    bridgeLeft: 0,
    bridgeWidth: SUBMENU_VISUAL_GAP,
    bridgeTop: 0,
    bridgeHeight: 0,
    ready: false,
  });

  useLayoutEffect(() => {
    const measure = () => {
      const panel = panelRef.current;
      if (!panel) return;

      const anchorRect = anchor.getBoundingClientRect();
      const surface = anchor.closest<HTMLElement>("[data-menu-surface]");
      const surfaceRect = surface?.getBoundingClientRect();
      const surfaceRight = surfaceRect?.right ?? anchorRect.right;
      const surfaceLeft = surfaceRect?.left ?? anchorRect.left;
      const margin = flyoutMargin(anchorRect.right, surfaceRight);
      const panelWidth = panel.offsetWidth;
      const rightLeft = anchorRect.right + margin;
      const fitsOnRight = rightLeft + panelWidth <= window.innerWidth - SUBMENU_EDGE_GAP;
      const panelLeft = fitsOnRight
        ? rightLeft
        : Math.max(SUBMENU_EDGE_GAP, surfaceLeft - SUBMENU_VISUAL_GAP - panelWidth);

      const limitBottom = getVisibleBottomBarRect()?.top ?? window.innerHeight;
      // scrollHeight stays the full content height when maxHeight clips the panel.
      // Add the border (offsetHeight - clientHeight) so the box we place matches what is painted.
      const menuHeight = panel.scrollHeight + (panel.offsetHeight - panel.clientHeight);
      const next = placeSubmenuAboveFooter(anchorRect.top, menuHeight, limitBottom);
      const visibleHeight = next.maxHeight ?? menuHeight;
      const bridge = hoverBridgeBox(next.offsetTop, visibleHeight, anchor.offsetHeight);
      const horizontal = fitsOnRight
        ? hoverBridgeHorizontal(anchorRect.right, surfaceRight, margin)
        : {
            left: panelLeft + panelWidth,
            width: Math.max(0, surfaceLeft - (panelLeft + panelWidth)),
          };

      setPlacement({
        panelLeft,
        panelTop: anchorRect.top + next.offsetTop,
        maxHeight: next.maxHeight,
        bridgeLeft: horizontal.left,
        bridgeWidth: horizontal.width,
        bridgeTop: anchorRect.top + bridge.top,
        bridgeHeight: bridge.height,
        ready: true,
      });
    };

    measure();
    window.addEventListener("resize", measure);
    document.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      document.removeEventListener("scroll", measure, true);
    };
  }, [anchor, panelRef]);

  return placement;
}

export function FlyoutPanel({
  anchor,
  zIndex,
  className,
  testId,
  onMouseEnter,
  onMouseLeave,
  children,
}: {
  anchor: HTMLDivElement;
  zIndex: number;
  className: string;
  testId?: string;
  /** Hover handoff for the desktop sidebar menu. Tap-driven menus leave these out. */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const placement = useAboveFooterPlacement(anchor, panelRef);
  const visibility = placement.ready ? "visible" : "hidden";

  return createPortal(
    <>
      <div
        data-actions-menu-layer=""
        style={{
          position: "fixed",
          left: placement.bridgeLeft,
          width: placement.bridgeWidth,
          top: placement.bridgeTop,
          height: placement.bridgeHeight,
          zIndex,
          visibility,
        }}
        onMouseEnter={onMouseEnter}
      />
      <div
        ref={panelRef}
        data-testid={testId}
        data-menu-surface
        data-actions-menu-layer=""
        className={className}
        style={{
          position: "fixed",
          left: placement.panelLeft,
          top: placement.panelTop,
          zIndex,
          maxHeight: placement.maxHeight,
          overflowY: placement.maxHeight != null ? "auto" : undefined,
          visibility,
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
